import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import Typography from '@mui/material/Typography/Typography';
import { useForgotPasswordMutation } from '@services/accounts';
import { handleError } from '@utils/common';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { AuthenticationSuccess } from 'src/routes/public/Authentication/AuthenticationSuccess';

const ForgotPasswordSuccess = () => {
    const { formatMessage } = useIntl();
    const [content, setContent] = useState({
        title: formatMessage({
            id: 'auth.forgotPassword.resetRequested.title',
            defaultMessage: 'Password Reset Requested',
        }),
        message: (
            <>
                <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                    <FormattedMessage
                        id="auth.forgotPassword.resetRequested.description"
                        defaultMessage="If an account exists for the email address you provided, a link to securely reset your password has been sent."
                        description="Success message after requesting password reset. Used in: /auth/forgot-password/success"
                    />
                </Typography>
                <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={2}>
                    <FormattedMessage
                        id="auth.forgotPassword.resetRequested.hint"
                        defaultMessage="Please check your inbox and your spam folder."
                        description="Hint to check email for password reset link. Used in: /auth/forgot-password/success"
                    />
                </Typography>
            </>
        ),
    });
    const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
    const { state: routeState } = useLocation();

    const handleResendLink = async () => {
        try {
            await forgotPassword({ email: routeState?.email }).unwrap();
            setContent({
                title: formatMessage({
                    id: 'auth.forgotPassword.resetResent.title',
                    defaultMessage: 'Password Reset Link Resent',
                }),
                message: (
                    <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                        <FormattedMessage
                            id="auth.forgotPassword.resetResent.description"
                            defaultMessage="Success! If an account exists, a new reset link has been sent."
                            description="Success message when password reset link is resent. Used in: /auth/forgot-password/success"
                        />
                    </Typography>
                ),
            });
        } catch (error: unknown) {
            handleError(
                formatMessage({
                    id: 'auth.forgotPassword.error',
                    defaultMessage: 'Forgot Password Error',
                }),
                error
            );
        }
    };

    return (
        <>
            <title>Victron Professional - Password Reset Success</title>
            <AuthenticationSuccess
                title={content.title}
                message={content.message}
                primaryButtonProps={{
                    children: 'Return to Login',
                    component: Link,
                    to: { pathname: '../login', search: window.location.search },
                }}
                secondaryButtonProps={{
                    children: 'Resend Link',
                    disabled: content.title === 'Password Reset Link Resent' || isLoading,
                    onClick: handleResendLink,
                    startIcon: isLoading ? <CircularProgress size={20} /> : undefined,
                }}
                stateVariable="forgot-password-allow"
            />
        </>
    );
};

export { ForgotPasswordSuccess };
